import { Locale } from "@dealer-center/ddc-core-utils";
import { Tabs } from "@dealer-center/core-components";
import { IncentivesTable } from "./IncentivesTable";
import type {
  IncentivesPayload,
  IncentivesRecord
} from "@dealer-center/ddc-pages-incentives-manager-interface";
import { useContext, useEffect, useState } from "react";
import { TableAttributesContext } from "../providers/TableAttributesProvider";
import styled from "styled-components";
import { TabActionPanel } from "./TabActionPanel";
import { getTabStates } from "../utils/utils";

interface TabData {
  label: string;
  value: string | number;
  active?: boolean;
}

interface TabProps {
  hasAnyUpstreamCompliance: boolean;
  incentives: null | IncentivesPayload;
  showIncentivePopup?: (
    isShowing: boolean,
    type: string,
    incentive?: IncentivesRecord
  ) => void;
  tabIndex: number;
  setTabIndex: (index: number) => void;
}

const IncentivesManagerTabBar = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const setupTableAttributes = (
  index: number,
  selectedAttributes,
  setTableAttributes
) => {
  const ta = selectedAttributes.filter(attribute => {
    return (index === 0 && attribute === "SOURCE") ||
      (index !== 1 && attribute === "ACTION_NEEDED")
      ? false
      : true;
  });
  setTableAttributes(ta);
};

export const TabContainer = ({
  hasAnyUpstreamCompliance,
  incentives,
  showIncentivePopup,
  tabIndex,
  setTabIndex
}: TabProps) => {
  const [tabs, setTabs] = useState<TabData[]>([]);
  const { selectedAttributes } = useContext(TableAttributesContext);
  const [tableAttributes, setTableAttributes] = useState<string[]>([]);
  const [tableSelection, setTableSelection] = useState<number[]>([]);

  useEffect(() => {
    const buildTabs = (incentives, tabIdx = tabIndex) => {
      if (incentives && incentives.logicalStates) {
        const tabData: TabData[] = getTabStates(
          incentives,
          hasAnyUpstreamCompliance
        ).map((state, index) => {
          return {
            active: index === tabIdx,
            label: `${Locale.getText(state.logicalState)} (${state.totalIncentives})`,
            value: index
          };
        });
        setupTableAttributes(tabIndex, selectedAttributes, setTableAttributes);
        return tabData;
      }
      return [];
    };

    if (incentives) {
      let tabidx = tabIndex;
      if (tabIndex === -1) {
        incentives.logicalStates.find((state, index) => {
          if (state.logicalState === "PUBLISHED") {
            setTabIndex(index);
            tabidx = index;
            return true;
          }
          return false;
        });
      }
      setTabs(buildTabs(incentives, tabidx));
    }
  }, [incentives, hasAnyUpstreamCompliance, selectedAttributes, tabIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const onTabActivate = (index: number) => {
    setTabIndex(index);
    setupTableAttributes(index, selectedAttributes, setTableAttributes);
  };

  const handleRowDoubleClick = (record: IncentivesRecord) => {
    if (!showIncentivePopup) return;

    const currentTab = incentives?.logicalStates[tabIndex]?.logicalState || "";

    // DECLINED tab doesn't have Edit or Open capability, so don't open anything
    if (currentTab === "DECLINED") {
      return;
    }

    const type = currentTab === "EXPIRED" ? "OPEN_INCENTIVE" : "EDIT_INCENTIVE";
    showIncentivePopup(true, type, record);
  };

  if (tabs.length === 0) {
    return null;
  }
  return (
    <IncentivesManagerTabBar>
      <Tabs id="tabs" tabs={tabs} onActivate={onTabActivate} />
      <TabActionPanel
        currentTab={incentives?.logicalStates[tabIndex]?.logicalState || ""}
        incentives={
          incentives?.logicalStates[tabIndex]?.incentives as IncentivesRecord[]
        }
        selections={tableSelection}
        showIncentivePopup={showIncentivePopup}
      />
      <IncentivesTable
        columns={tableAttributes}
        data={
          incentives?.logicalStates[tabIndex]?.incentives as IncentivesRecord[]
        }
        onRowDoubleClick={handleRowDoubleClick}
        onSelections={selectedIndices => {
          setTableSelection(selectedIndices);
        }}
      />
    </IncentivesManagerTabBar>
  );
};
